import { useMemo } from 'react';

import {
  CmsApiTileGridSection,
  CmsTileGridTile,
} from 'modules/cms/api/types/CmsApiTileGrid';
import { camelCaseToDash } from 'utils/string';

export type IdentifiedTileGridBlockTile = CmsTileGridTile & {
  tileDOMElementId: string;
  tileSlug: string;
  tileIndex: number;
  sectionIndex: number;
};

export type IdentifiedTileGridBlockSection = Omit<
  CmsApiTileGridSection,
  'tiles'
> & {
  identifiedTiles: IdentifiedTileGridBlockTile[];
  sectionSlug: string;
  sectionIndex: number;
};

function createIdentifedTile(
  tile: CmsTileGridTile,
  {
    tileIndex,
    sectionIndex,
    blockId,
  }: { tileIndex: number; sectionIndex: number; blockId: string }
): IdentifiedTileGridBlockTile {
  const headingSlug = camelCaseToDash(tile.heading);
  const tileSlug = `${headingSlug}-${tileIndex}`.replaceAll(' ', '-');
  const tileElementId = `tile-grid-${blockId}-${sectionIndex}-${tileIndex}`;

  return {
    ...tile,
    tileDOMElementId: tileElementId,
    tileSlug,
    tileIndex,
    sectionIndex,
  };
}

function createIdentifiedSection(
  section: CmsApiTileGridSection,
  sectionIndex: number,
  blockId: string
): IdentifiedTileGridBlockSection {
  const nameSlug = section.name
    ? camelCaseToDash(section.name).replaceAll(' ', '-')
    : '';

  const sectionSlug = [nameSlug, sectionIndex].filter(Boolean).join('-');

  const identifiedTiles = section.tiles.map((tile, tileIndex) =>
    createIdentifedTile(tile, { tileIndex, sectionIndex, blockId })
  );

  return { ...section, sectionSlug, identifiedTiles, sectionIndex };
}

type Args = {
  blockId: string;
  sections: CmsApiTileGridSection[];
};

export function useIdentifiedTileGridBlockData({ blockId, sections }: Args) {
  const identifiedSections = useMemo(
    () =>
      sections.map((section, sectionIndex) =>
        createIdentifiedSection(section, sectionIndex, blockId)
      ),
    [blockId, sections]
  );

  const identifiedSectionBySlug = useMemo(() => {
    const bySlug: Record<string, IdentifiedTileGridBlockSection> = {};

    identifiedSections.forEach((section) => {
      bySlug[section.sectionSlug] = section;
    });

    return bySlug;
  }, [identifiedSections]);

  const identifiedTiles = identifiedSections.flatMap(
    (section) => section.identifiedTiles
  );

  const identifiedTileBySlug = useMemo(() => {
    const bySlug: Record<string, IdentifiedTileGridBlockTile> = {};

    identifiedTiles.forEach((tile) => {
      bySlug[tile.tileSlug] = tile;
    });

    return bySlug;
  }, [identifiedTiles]);

  return {
    identifiedSections,
    identifiedSectionBySlug,
    identifiedTiles,
    identifiedTileBySlug,
  };
}
